import React, { useState } from 'react';
import {
  X,
  Mail,
  Link2,
  MessageCircle,
  Check,
  Heart,
  Share2,
} from 'lucide-react';
import {
  handleNativeShare,
  generateEmailLink,
  copyToClipboard,
  generateTwitterLink,
  generateLinkedInLink,
  SHARE_CONTENT,
} from '../../services/shareService';

interface ReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ReferralModal: React.FC<ReferralModalProps> = ({ isOpen, onClose }) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<'share' | 'feedback'>('share');
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  if (!isOpen) return null;

  const handleCopyLink = async () => {
    const success = await copyToClipboard(SHARE_CONTENT.url);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2500);
    }
  };

  const handleShare = async () => {
    const shared = await handleNativeShare();
    if (!shared) {
      // Fallback to copy
      handleCopyLink();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden relative animate-modal-enter"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-maple-600 to-maple-700 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-lg"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-7 h-7" />
            <h2 className="text-xl font-bold">Help Us Help Others</h2>
          </div>
          <p className="text-white/90 text-sm">
            Share with someone who might need funding support
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('share')}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              activeTab === 'share'
                ? 'text-maple-600 border-b-2 border-maple-600 bg-maple-50'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Share2 size={16} className="inline mr-2" />
            Share
          </button>
          <button
            onClick={() => setActiveTab('feedback')}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              activeTab === 'feedback'
                ? 'text-maple-600 border-b-2 border-maple-600 bg-maple-50'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <MessageCircle size={16} className="inline mr-2" />
            Feedback
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'share' ? (
            <div className="space-y-4">
              <p className="text-slate-600 text-sm mb-4">
                Know someone looking for grants? Share this tool with them:
              </p>

              {/* Share Buttons */}
              <div className="grid grid-cols-1 gap-3">
                {/* Native Share (mobile) or Email */}
                {canShare ? (
                  <button
                    onClick={handleShare}
                    className="w-full flex items-center justify-center gap-2 bg-maple-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-maple-700 transition-colors"
                  >
                    <Share2 size={20} />
                    Share App
                  </button>
                ) : (
                  <a
                    href={generateEmailLink()}
                    className="w-full flex items-center justify-center gap-2 bg-maple-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-maple-700 transition-colors"
                  >
                    <Mail size={20} />
                    Send Email Invite
                  </a>
                )}

                {/* Copy Link */}
                <button
                  onClick={handleCopyLink}
                  className={`w-full flex items-center justify-center gap-2 border-2 py-3 px-4 rounded-xl font-semibold transition-all ${
                    copySuccess
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-slate-300 text-slate-700 hover:border-maple-300 hover:bg-maple-50'
                  }`}
                >
                  {copySuccess ? (
                    <>
                      <Check size={20} />
                      Link Copied!
                    </>
                  ) : (
                    <>
                      <Link2 size={20} />
                      Copy Link
                    </>
                  )}
                </button>

                {/* Social Links */}
                <div className="flex gap-3 pt-2">
                  <a
                    href={generateTwitterLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-slate-800 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-slate-900 transition-colors"
                  >
                    X / Twitter
                  </a>
                  <a
                    href={generateLinkedInLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-700 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-blue-800 transition-colors"
                  >
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wide mb-2">
                  Your Feedback Matters
                </h3>
                <p className="text-sm text-slate-700 mb-4">
                  Found a bug? Have a suggestion? We'd love to hear from you!
                </p>
                <a
                  href="mailto:feedback@granthub.ca?subject=Grant Hub Feedback"
                  className="inline-flex items-center gap-2 text-maple-600 font-semibold hover:text-maple-700 transition-colors"
                >
                  <Mail size={18} />
                  Send Feedback
                </a>
              </div>

              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
                  What would help you?
                </h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-maple-500">•</span>
                    More grant categories?
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-maple-500">•</span>
                    Better search filters?
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-maple-500">•</span>
                    Application tracking features?
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
